import { type ReactNode, useEffect } from 'react';
import { type DocumentNode, useQuery } from '@apollo/client';

import useUrlInfo, { type PartnerInfo } from '../../utils/urls/useUrlInfo';
import GET_PARTNER_CONFIGURATION from './queries/GET_APPLICATION_CONFIGURATION';
import SettingsInitializer from './SettingsInitializer';
import type { Configuration } from '../../../../types/Configuration';
import useAppConfig from 'canvas_states/config/useAppConfig';

export interface PartnerConfigProviderProps
  extends Pick<PartnerInfo, 'pid' | 'requestId'>,
    Partial<Omit<PartnerInfo, 'pid' | 'requestId'>> {
  query?: DocumentNode;
  children: ReactNode;
}

interface QueryParameter {
  pid: string;
  requestId: string;
  affiliateId: string;
  overrideChannelSource: string | null | undefined;
}

interface QueryResponse {
  getApplicationConfiguration: Configuration;
}

/**
 * `PartnerInfo` must be provided via props when consuming `PartnerConfigProvider` directly
 * otherwise please use the `default` `export`
 */
export const PartnerConfigProvider = ({
  children,
  query = GET_PARTNER_CONFIGURATION,
  ...partner
}: PartnerConfigProviderProps) => {
  const { pid, requestId, language, currency, overrideChannelSource } = partner;

  const { loading, data } = useQuery<QueryResponse, QueryParameter>(query, {
    variables: { pid, affiliateId: '', requestId, overrideChannelSource },
  });

  const {
    actions: { setConfiguration },
    selectors: { isInitialized },
  } = useAppConfig();

  useEffect(() => {
    if (loading) return;
    const configuration = data?.getApplicationConfiguration;

    if (!configuration) {
      throw new Error('Missing app config !');
    }

    setConfiguration({
      ...configuration,
      language: language ?? configuration.language,
      currency: currency ?? configuration.currency,
    });
  }, [data, language, currency, setConfiguration, loading]);

  if (loading || !isInitialized()) return <></>;

  return <SettingsInitializer {...partner}>{children}</SettingsInitializer>;
};

const PartnerConfigProviderWithUrlInfo = ({ children }: { children: ReactNode }) => {
  const partnerInfo = useUrlInfo().states;
  return <PartnerConfigProvider {...partnerInfo}>{children}</PartnerConfigProvider>;
};

export default PartnerConfigProviderWithUrlInfo;
